/** Тонкая типизированная обёртка над официальным SDK `telegram-web-app.js`. */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { first_name?: string } };
  platform: string;
  ready(): void;
  expand(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  disableVerticalSwipes?(): void;
  openTelegramLink?(url: string): void;
  openLink?(url: string, options?: { try_instant_view?: boolean }): void;
  close?(): void;
  isVersionAtLeast?(version: string): boolean;
  shareToStory?(mediaUrl: string, params?: { text?: string }): void;
  downloadFile?(params: { url: string; file_name: string }, cb?: (accepted: boolean) => void): void;
  BackButton?: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred?(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

const BG = '#05070C';

export function webApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * initData для заголовка `Authorization: tma …`.
 * Вне Telegram — только в dev и только из VITE_DEV_INIT_DATA (подписанная строка, см. README).
 */
export function getInitData(): string | null {
  const fromTg = webApp()?.initData;
  if (fromTg) return fromTg;
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_INIT_DATA) return import.meta.env.VITE_DEV_INIT_DATA as string;
  return null;
}

export function initTelegram(): void {
  const tg = webApp();
  if (!tg) return;
  tg.ready();
  tg.expand();
  tg.setHeaderColor?.(BG);
  tg.setBackgroundColor?.(BG);
  tg.setBottomBarColor?.(BG);
  tg.disableVerticalSwipes?.();
}

export function haptic(kind: 'tap' | 'select' = 'tap'): void {
  const h = webApp()?.HapticFeedback;
  if (!h) return;
  if (kind === 'select') h.selectionChanged();
  else h.impactOccurred('light');
}

/** Параметр запуска мини-аппа (`?startapp=…`): рефка `r_…` или возврат с оплаты `pay_<id>`. */
export function getStartParam(): string | null {
  const fromTg = webApp()?.initDataUnsafe?.start_param;
  if (fromTg) return fromTg;
  const raw = getInitData();
  const fromInit = raw ? new URLSearchParams(raw).get('start_param') : null;
  if (fromInit) return fromInit;
  // Кнопка из бота открывает мини-апп с ?startapp=… в адресе.
  try {
    return new URLSearchParams(window.location.search).get('startapp');
  } catch {
    return null;
  }
}

/** Открыть ссылку оплаты: t.me — внутри Telegram, остальное — во внешнем браузере. */
export function openPayUrl(url: string): void {
  const tg = webApp();
  if (url.startsWith('https://t.me/') && tg?.openTelegramLink) tg.openTelegramLink(url);
  else if (tg?.openLink) tg.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** Правовые страницы (статика мини-аппа, apps/miniapp/public/legal): в браузере Telegram, мини-апп не закрывается. */
export const LEGAL = { terms: '/legal/terms.html', privacy: '/legal/privacy.html' } as const;

export function openLegal(page: keyof typeof LEGAL): void {
  const url = new URL(LEGAL[page], window.location.origin).toString();
  const tg = webApp();
  if (tg?.openLink) tg.openLink(url, { try_instant_view: false });
  else window.open(url, '_blank', 'noopener');
}

export function hapticResult(type: 'success' | 'error'): void {
  webApp()?.HapticFeedback?.notificationOccurred?.(type);
}

/** Системная кнопка «Назад» Telegram. Возвращает функцию снятия. */
export function bindBackButton(onBack: () => void): () => void {
  const bb = webApp()?.BackButton;
  if (!bb) return () => {};
  bb.onClick(onBack);
  bb.show();
  return () => {
    bb.offClick(onBack);
    bb.hide();
  };
}

/** Свернуть мини-апп (результат придёт в бот). */
export function closeApp(): boolean {
  const tg = webApp();
  if (!tg?.close) return false;
  tg.close();
  return true;
}

/** Скачать файл: нативно в Telegram (Bot API 8.0+), иначе — открыть ссылку. */
export function downloadFile(url: string, fileName: string): void {
  const tg = webApp();
  if (tg?.downloadFile && tg.isVersionAtLeast?.('8.0')) tg.downloadFile({ url, file_name: fileName });
  else if (tg?.openLink) tg.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** Можно ли выложить в сторис (Bot API 7.8+). */
export function canShareToStory(): boolean {
  const tg = webApp();
  return Boolean(tg?.shareToStory && tg.isVersionAtLeast?.('7.8'));
}

export function shareToStory(url: string): void {
  webApp()?.shareToStory?.(url, { text: 'Сделано в Trendator ✨' });
}

/** Открыть t.me-ссылку внутри Telegram (иначе — в новой вкладке). */
export function openTelegram(url: string): void {
  const tg = webApp();
  if (tg?.openTelegramLink && url.startsWith('https://t.me/')) tg.openTelegramLink(url);
  else if (tg?.openLink) tg.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** «Отправить в Telegram»: системный выбор чата с готовым текстом и ссылкой. */
export function shareLink(url: string, text: string): void {
  openTelegram(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
}

/** Скопировать в буфер. true — получилось. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      el.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
